import { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../hooks/useNotifications';
import { supabase } from '../lib/supabase';
import type { BusinessProfile } from '../types/database.types';
import { motion, AnimatePresence } from 'framer-motion';
import { usePermissions } from '../hooks/usePermissions';
import { useTranslation } from 'react-i18next';

import {
  XMarkIcon,
  Bars3Icon,
} from '@heroicons/react/24/outline';
import {
  Store,
  Tag,
  Ticket,
  ClipboardList,
  User,
  Phone,
  Mail,
  Settings,
  Clock,
  Sparkles,
  ClipboardCheck,
  Bell,
  BarChart2,
  Home,
  Eye,
  Share2, // New
  Moon,
  Sun,
  Globe,
  Check,
  Lock,
  Calculator
} from 'lucide-react';

import NotificationBanner from './NotificationBanner';
import NotificationsPanel from './NotificationsPanel';

// Configuración de Ítems de Navegación Personalizables
const NAV_ITEMS_CONFIG: Record<string, {
  id: string;
  name: string;
  path: string;
  icon: any;
  color: string;
  bgColor: string;
  strokeWidthActive: number;
  feature?: string;
}> = {
  dashboard: { id: 'dashboard', name: 'Panel', path: '/dashboard', icon: BarChart2, color: 'text-blue-600', bgColor: 'bg-blue-50', strokeWidthActive: 2.5, feature: 'stats' },
  catalog: { id: 'catalog', name: 'Catálogo', path: '/catalog', icon: Store, color: 'text-sky-600', bgColor: 'bg-sky-50', strokeWidthActive: 2.5, feature: 'catalog' },
  orders: { id: 'orders', name: 'Pedidos', path: '/orders', icon: ClipboardCheck, color: 'text-blue-600', bgColor: 'bg-blue-50', strokeWidthActive: 2.5, feature: 'orders' },
  categories: { id: 'categories', name: 'Categorías', path: '/categories', icon: Tag, color: 'text-orange-600', bgColor: 'bg-orange-50', strokeWidthActive: 2.5, feature: 'catalog' },
  inventory: { id: 'inventory', name: 'Inventario', path: '/inventory', icon: ClipboardList, color: 'text-cyan-600', bgColor: 'bg-cyan-50', strokeWidthActive: 2.5, feature: 'inventory' },
  marketing: { id: 'marketing', name: 'Marketing', path: '/marketing', icon: Share2, color: 'text-purple-600', bgColor: 'bg-purple-50', strokeWidthActive: 2.5, feature: 'allow_marketing' },
  coupons: { id: 'coupons', name: 'Cupones', path: '/coupons', icon: Ticket, color: 'text-green-600', bgColor: 'bg-green-50', strokeWidthActive: 2.5, feature: 'coupons' },
  calculator: { id: 'calculator', name: 'Escandallo', path: '/calculator', icon: Calculator, color: 'text-rose-600', bgColor: 'bg-rose-50', strokeWidthActive: 2.5 },
  profile: { id: 'profile', name: 'Perfil', path: '/profile', icon: User, color: 'text-gray-600', bgColor: 'bg-gray-50', strokeWidthActive: 2.5 },
};

function Layout() {
  const { t, i18n } = useTranslation();
  const { user, loading: authLoading, isImpersonating, stopImpersonation } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { unreadCount: notificationsUnreadCount } = useNotifications();
  const [businessProfile, setBusinessProfile] = useState<BusinessProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showPlansModal, setShowPlansModal] = useState(false);

  // Settings for platform
  const [showPlatformSettings, setShowPlatformSettings] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    return (localStorage.getItem('theme') as 'light' | 'dark') || 'light';
  });
  const [language, setLanguage] = useState<'es' | 'en'>(() => {
    return (localStorage.getItem('language') as 'es' | 'en') || 'es';
  });

  // Apply Theme
  useEffect(() => {
    const root = window.document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);
  }, [theme]);

  // Apply Language
  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
    i18n.changeLanguage(language);
  }, [language, i18n]);
  const [selectedCountry, setSelectedCountry] = useState<'cuba' | 'world'>('world'); // Removed unused state warnings if possible, but keeping for safety
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationsSupported, setNotificationsSupported] = useState(false);
  const [unviewedOrdersCount, setUnviewedOrdersCount] = useState(0);

  // --- STATE FOR CUSTOMIZABLE NAVIGATION ---
  const [bottomNavIds, setBottomNavIds] = useState<string[]>(['dashboard', 'orders', 'categories']);
  const [drawerNavIds, setDrawerNavIds] = useState<string[]>(['marketing', 'inventory', 'coupons', 'profile', 'calculator']);
  const [isCustomizeMode, setIsCustomizeMode] = useState(false);

  // Load preferences
  useEffect(() => {
    const saved = localStorage.getItem('skymenu_nav_prefs');
    if (saved) {
      try {
        const { bottom, drawer } = JSON.parse(saved);
        if (bottom && drawer && Array.isArray(bottom) && Array.isArray(drawer)) {
          // Validate IDs exist in config to avoid crashes if config changes
          const validBottom = bottom.filter(id => NAV_ITEMS_CONFIG[id] && id !== 'catalog');
          const validDrawer = drawer.filter(id => NAV_ITEMS_CONFIG[id] && id !== 'catalog');
          
          // Inject missing items (like newly added tools) into the drawer
          const allSavedIds = [...validBottom, ...validDrawer, 'catalog'];
          const missingIds = Object.keys(NAV_ITEMS_CONFIG).filter(id => !allSavedIds.includes(id));
          
          if (validBottom.length > 0) {
            setBottomNavIds(validBottom);
            setDrawerNavIds([...validDrawer, ...missingIds]);
          }
        }
      } catch (e) {
        console.error("Error loading nav prefs", e);
      }
    }
  }, []);

  // Save preferences
  const saveNavPrefs = (newBottom: string[], newDrawer: string[]) => {
    localStorage.setItem('skymenu_nav_prefs', JSON.stringify({ bottom: newBottom, drawer: newDrawer }));
  };

  const handleMoveToDrawer = (id: string) => {
    // Prevent emptying bottom bar too much (optional, but good UX to keep at least 1)
    if (bottomNavIds.length <= 1) return;

    const newBottom = bottomNavIds.filter(i => i !== id);
    const newDrawer = [...drawerNavIds, id];
    setBottomNavIds(newBottom);
    setDrawerNavIds(newDrawer);
    saveNavPrefs(newBottom, newDrawer);
  };

  const handleMoveToBottom = (id: string) => {
    if (bottomNavIds.length >= 3) {
      // Optional: Shake animation or toast "Máximo 3 ítems"
      // For now just return
      return;
    }
    const newDrawer = drawerNavIds.filter(i => i !== id);
    const newBottom = [...bottomNavIds, id];
    setBottomNavIds(newBottom);
    setDrawerNavIds(newDrawer);
    saveNavPrefs(newBottom, newDrawer);
  };
  // ------------------------------------------

  // Hook de permisos
  const { canAccess, permissions } = usePermissions();

  useEffect(() => {
    if (user) {
      loadBusinessProfile();
      enableNotifications(); // Activar notificaciones automáticamente
      loadUnviewedOrdersCount();
      setupOrdersSubscription();

      // Suscribirse a cambios en tiempo real del perfil del negocio
      const profileSubscription = supabase
        .channel(`layout:business_profile:${user.id}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'business_profiles',
            filter: `id=eq.${user.id}`
          },
          () => {
            console.log('🔄 Business Profile updated in real-time');
            loadBusinessProfile();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(profileSubscription);
      };
    } else {
      setBusinessProfile(null);
      setLoading(false);
    }
  }, [user]);

  async function loadUnviewedOrdersCount() {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user?.id)
        .eq('viewed', false);

      if (error) throw error;
      setUnviewedOrdersCount(data?.length || 0);
    } catch (error) {
      console.error('Error loading unviewed orders count:', error);
    }
  }

  function setupOrdersSubscription() {
    if (!user) return;

    const subscription = supabase
      .channel('orders-badge')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'orders',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          setUnviewedOrdersCount(prev => prev + 1);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'orders',
          filter: `user_id=eq.${user.id}`
        },
        (payload: any) => {
          if (payload.new.viewed && !payload.old.viewed) {
            setUnviewedOrdersCount(prev => Math.max(0, prev - 1));
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }

  async function loadBusinessProfile() {
    try {
      const { data, error } = await supabase
        .from('business_profiles')
        .select('*')
        .eq('id', user?.id)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      setBusinessProfile(data);
    } catch (error) {
      console.error('Error cargando perfil del negocio:', error);
    } finally {
      setLoading(false);
    }
  }

  async function enableNotifications() {
    if (!user) return;

    try {
      // Verificar soporte de notificaciones
      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        return;
      }

      // Solicitar permiso para notificaciones si no se ha hecho
      if (Notification.permission === 'default') {
        await Notification.requestPermission();
      }

      if (Notification.permission !== 'granted') {
        return;
      }

      // Registrar el service worker si no está registrado
      if (!navigator.serviceWorker.controller) {
        await navigator.serviceWorker.register('/sw.js');
      }

      const registration = await navigator.serviceWorker.ready;

      // Verificar si ya hay una suscripción
      const existingSubscription = await registration.pushManager.getSubscription();

      if (existingSubscription) {
        return; // Ya está suscrito
      }

      // Crear una nueva suscripción
      const vapidPublicKey = 'BLBz5U9-ld_EXMzQX4uDUEfzG4nXji6c9_KBKvVNTilqwN9W7tP9SXVaCO-_4-MhKcGZqoRKJkUOQlrJBIH0V8U';
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Guardar la suscripción en la base de datos
      await supabase
        .from('push_subscriptions')
        .upsert({
          user_id: user.id,
          subscription: JSON.stringify(subscription)
        }, {
          onConflict: 'user_id'
        });
    } catch (error) {
      console.error('Error al activar notificaciones:', error);
    }
  }

  // Función auxiliar para convertir la clave VAPID a Uint8Array
  function urlBase64ToUint8Array(base64String: string) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }

    return outputArray;
  }

  const allNavigationItems = user ? [
    { name: 'Catálogo', path: '/catalog', icon: Store, feature: 'catalog' },
    { name: 'Dashboard', path: '/dashboard', icon: BarChart2, feature: 'stats' },
    { name: 'Categorías', path: '/categories', icon: Tag, feature: 'catalog' },
    { name: 'Cupones', path: '/coupons', icon: Ticket, feature: 'coupons' },
    { name: 'Pedidos', path: '/orders', icon: ClipboardCheck, feature: 'orders' },
    { name: 'Inventario', path: '/inventory', icon: ClipboardList, feature: 'inventory' },
    { name: 'Marketing', path: '/marketing', icon: Share2, feature: 'allow_marketing' },
    { name: 'Escandallo', path: '/calculator', icon: Calculator, feature: undefined },
    { name: 'Perfil', path: '/profile', icon: User, feature: undefined },
    { name: 'Config.Store', path: '/settings', icon: Settings, feature: undefined },
  ] : [];

  const navigationItems = allNavigationItems.filter(item => {
    // Hide Marketing for Palladium plan
    if (item.path === '/marketing' && businessProfile?.subscription_plan === 'palladium') {
      return false;
    }
    return !item.feature || canAccess(item.feature);
  });

  const handlePlansClick = () => {
    navigate('/pricing');
    setMobileMenuOpen(false); // Cerrar menú móvil si está abierto
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-300 lg:flex">
      {/* --- DESKTOP SIDEBAR --- */}
      {user && location.pathname !== '/login' && (
        <aside className="hidden lg:flex flex-col w-64 fixed inset-y-0 left-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 z-50 shadow-sm">
          <div className="p-6 pb-4">
            <Link to="/account" className="flex items-center space-x-3">
              {businessProfile?.logo_url ? (
                <>
                  <img src={businessProfile.logo_url} className="h-8 w-8 rounded-full object-cover" />
                  <span className="text-xl font-bold text-sky-700 dark:text-sky-400 truncate">
                    {businessProfile.business_name}
                  </span>
                </>
              ) : (
                <span className="text-xl font-bold text-sky-700 dark:text-sky-400">SkyMenu</span>
              )}
            </Link>
          </div>
          <div className="px-6 mb-6 flex items-center gap-3">
             <div className="w-10 h-10 rounded-lg bg-sky-600 text-white flex items-center justify-center font-bold text-lg flex-shrink-0">
               {businessProfile?.business_name?.substring(0, 2).toUpperCase() || 'AD'}
             </div>
             <div className="overflow-hidden">
                <p className="font-bold text-sm text-gray-900 dark:text-white truncate">{user?.email?.split('@')[0] || 'Cargando...'}</p>
                <p className="text-xs text-gray-500 truncate">Administrador</p>
             </div>
          </div>
          <nav className="flex-1 px-4 space-y-1 overflow-y-auto pb-4">
             {navigationItems.map(item => {
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-sky-50 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 font-medium border-r-4 border-sky-600'
                        : 'text-slate-600 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-700/50 hover:text-sky-600 dark:hover:text-sky-400'
                    }`}
                  >
                    <item.icon className={`w-5 h-5 mr-3 ${isActive ? 'text-sky-600 dark:text-sky-400' : 'text-slate-400 dark:text-gray-400'}`} />
                    {item.name}
                    {item.path === '/orders' && unviewedOrdersCount > 0 && (
                      <span className="ml-auto bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                        {unviewedOrdersCount > 9 ? '9+' : unviewedOrdersCount}
                      </span>
                    )}
                  </Link>
                );
             })}
          </nav>
          <div className="p-4 border-t border-gray-100 dark:border-gray-700">
             <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="flex w-full items-center px-4 py-3 text-slate-600 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-700/50 rounded-lg transition-colors"
             >
                <Bell className="w-5 h-5 mr-3 text-slate-400" />
                <span>Notificaciones</span>
                {notificationsUnreadCount > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                    {notificationsUnreadCount}
                  </span>
                )}
             </button>
             {businessProfile?.subscription_plan === 'palladium' && (
                <Link
                  to="/pos"
                  className="flex w-full items-center justify-center px-4 py-3 mt-2 bg-slate-900 text-amber-400 rounded-lg border border-slate-700 shadow-sm hover:shadow-md transition-shadow"
                >
                   <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                   </svg>
                   <span className="font-bold">Terminal POS</span>
                </Link>
             )}
          </div>
        </aside>
      )}

      {/* --- MAIN CONTENT WRAPPER --- */}
      <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${user && location.pathname !== '/login' ? 'lg:pl-64' : ''}`}>
      {isImpersonating && (
        <div className="bg-amber-600 text-white px-4 py-2 text-sm font-medium flex justify-between items-center relative z-50 shadow-md">
          <div className="flex items-center gap-2">
            <Eye className="h-5 w-5" />
            <span>⚠️ Estás viendo el sistema como: <span className="font-bold underline">{businessProfile?.business_name || 'Usuario Suplantado'}</span></span>
          </div>
          <button
            onClick={stopImpersonation}
            className="bg-white text-amber-800 px-3 py-1 rounded hover:bg-amber-50 text-xs font-bold uppercase transition-colors shadow-sm"
          >
            Salir del Modo Cliente
          </button>
        </div>
      )}
      <nav className={`bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 fixed w-full top-0 z-50 transition-colors duration-300 ${user && location.pathname !== '/login' ? 'lg:hidden' : ''}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-8">
              <Link to={user ? "/account" : "/"} className="flex items-center space-x-3">
                {authLoading ? (
                  <div className="h-8 w-8 rounded-full bg-gray-200 animate-pulse" />
                ) : user ? (
                  businessProfile?.logo_url ? (
                    <>
                      <img
                        src={businessProfile.logo_url}
                        alt="Logo"
                        className="h-8 w-8 rounded-full object-cover"
                      />
                      <span className="text-xl font-bold bg-gradient-to-r from-sky-600 to-blue-600 bg-clip-text text-transparent">
                        {businessProfile?.business_name}
                      </span>
                    </>
                  ) : (
                    // Show skeleton while profile loads if user exists but profile not yet
                    <div className="flex items-center space-x-3">
                      <div className="h-8 w-8 rounded-full bg-gray-200 animate-pulse" />
                      <div className="h-6 w-32 bg-gray-200 rounded animate-pulse" />
                    </div>
                  )
                ) : (
                  <img
                    src="https://imgur.com/WY80a5F.png"
                    alt="SkyMenu Logo"
                    width="64"
                    height="64"
                    className="h-16 w-auto"
                  />
                )}
              </Link>

              {!user && (
                <>
                  <Link
                    to="/"
                    className="hidden md:flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-sky-600 hover:bg-sky-50 rounded-md transition-colors"
                  >
                    <Home className="h-5 w-5 text-sky-500" />
                    <span>Inicio</span>
                  </Link>
                  <button
                    onClick={handlePlansClick}
                    className="hidden md:flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-sky-600 hover:bg-sky-50 rounded-md transition-colors"
                  >
                    <Sparkles className="h-5 w-5 text-sky-500" />
                    <span>Planes</span>
                  </button>
                </>
              )}

              <div className="hidden sm:flex sm:space-x-8">
                {navigationItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center px-3 py-2 rounded-md relative transition-colors ${location.pathname === item.path
                      ? 'text-primary-600 dark:text-sky-400 bg-primary-50 dark:bg-sky-900/30'
                      : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                  >
                    <item.icon className={`h-5 w-5 mr-2 ${location.pathname === item.path
                      ? 'text-blue-500 dark:text-sky-400'
                      : 'text-gray-500 dark:text-gray-400'
                      }`} />
                    {item.name}
                    {item.path === '/orders' && unviewedOrdersCount > 0 && (
                      <span className="absolute -top-1 -right-1 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center" style={{ backgroundColor: 'var(--primary-color)' }}>
                        {unviewedOrdersCount > 9 ? '9+' : unviewedOrdersCount}
                      </span>
                    )}
                  </Link>
                ))}

                {/* BOTÓN POS - SOLO PALLADIUM */}
                {businessProfile?.subscription_plan === 'palladium' && (
                  <Link
                    to="/pos"
                    className="flex items-center px-3 py-2 rounded-md transition-all duration-300 relative group overflow-hidden bg-slate-900 text-amber-400 hover:text-amber-300 hover:shadow-[0_0_15px_rgba(251,191,36,0.3)] border border-slate-700"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-orange-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <svg className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <span className="font-bold tracking-wide">POS Terminal</span>
                    <span className="absolute -top-1 -right-1 flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
                    </span>
                  </Link>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {user && (
                <>
                  <button
                    onClick={() => setShowNotifications(!showNotifications)}
                    className="relative flex items-center px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-sky-600 dark:hover:text-sky-400 hover:bg-sky-50 dark:hover:bg-slate-700 rounded-md transition-colors"
                    title="Notificaciones"
                  >
                    <Bell className="h-5 w-5 text-sky-500 dark:text-sky-400" />
                    {notificationsUnreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                        {notificationsUnreadCount > 9 ? '9+' : notificationsUnreadCount}
                      </span>
                    )}
                  </button>

                  <button
                    onClick={() => setShowPlatformSettings(true)}
                    className="relative flex items-center px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-sky-600 dark:hover:text-sky-400 hover:bg-sky-50 dark:hover:bg-slate-700 rounded-md transition-colors sm:hidden"
                    title="Ajustes de Plataforma"
                  >
                    <Settings className="h-5 w-5 text-sky-500 dark:text-sky-400" />
                  </button>
                </>
              )}



              {user && (
                <Link
                  to="/pricing"
                  className="flex items-center px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-sky-600 dark:hover:text-sky-400 hover:bg-sky-50 dark:hover:bg-slate-700 rounded-md transition-colors font-medium border border-sky-200 dark:border-sky-800"
                >
                  <Sparkles className="h-5 w-5 mr-2 text-sky-500 dark:text-sky-400" />
                  <span className="hidden sm:inline">Planes</span>
                </Link>
              )}

              {!user && (
                <Link
                  to="/login"
                  className="flex items-center px-4 py-2 bg-gradient-to-r from-sky-600 to-blue-600 text-white rounded-full hover:shadow-lg hover:scale-105 transition-all font-medium"
                >
                  <User className="h-5 w-5 mr-2" />
                  <span className="hidden sm:inline">Iniciar Sesión</span>
                </Link>
              )}

              <div className="flex items-center sm:hidden">
                {/* Mobile Menu Trigger removed - moved to bottom bar */}
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Bottom Navigation Bar (Fixed) */}
        <div className={`sm:hidden fixed bottom-4 left-4 right-4 z-50 transition-all duration-300 pointer-events-none ${location.pathname === '/login' ? 'hidden' : ''} ${mobileMenuOpen || showPlatformSettings ? 'translate-y-24 opacity-0' : 'translate-y-0 opacity-100'}`}>
          <div className="flex justify-between items-center bg-white/75 dark:bg-gray-900/75 backdrop-blur-lg shadow-xl shadow-sky-900/5 border border-white/40 dark:border-gray-700/50 rounded-full px-2 py-1 pointer-events-auto">
            {(() => {
              const renderNavItem = (id: string, isFixed = false) => {
                const item = NAV_ITEMS_CONFIG[id];
                if (!item) return null;

                // Check permissions
                if (item.feature && !canAccess(item.feature as any)) {
                  return null;
                }

                // Hide Marketing for Palladium
                if (id === 'marketing' && businessProfile?.subscription_plan === 'palladium') return null;

                const isActive = location.pathname === item.path;
                const isOrders = id === 'orders';

                if (isCustomizeMode && !isFixed) {
                  return (
                    <button
                      key={id}
                      onClick={() => handleMoveToDrawer(id)}
                      className="flex flex-col items-center justify-center w-full py-1.5 relative animate-pulse"
                    >
                      <div className="relative p-1 border border-red-300 rounded-lg bg-red-50 dark:bg-red-900/40">
                        <item.icon className="h-5 w-5 text-red-500 mb-0.5" strokeWidth={2} />
                        <div className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center shadow-sm">
                          <span className="text-[10px] font-bold">-</span>
                        </div>
                      </div>
                      <span className="text-[9px] font-medium text-red-500 drop-shadow-sm mt-0.5">Ocultar</span>
                    </button>
                  );
                }

                if (isFixed && id === 'catalog') {
                  return (
                    <Link
                      key={id}
                      to={item.path}
                      className={`flex flex-col items-center justify-center w-full py-1.5 relative transition-all duration-300 hover:scale-105 ${isActive ? 'scale-105' : ''}`}
                    >
                      <div className="flex flex-col items-center justify-center">
                        <div className="relative">
                          <item.icon className={`h-6 w-6 transition-colors ${isActive ? 'text-blue-600 dark:text-sky-400' : 'text-gray-500 dark:text-gray-400'}`} strokeWidth={isActive ? 2.5 : 2} />
                        </div>
                        <span className={`text-[9px] transition-colors mt-1 ${isActive ? 'text-blue-600 dark:text-sky-400 font-bold' : 'text-gray-500 dark:text-gray-400 font-medium'}`}>{item.name}</span>
                      </div>
                    </Link>
                  );
                }

                return (
                  <Link
                    key={id}
                    to={item.path}
                    className={`flex flex-col items-center justify-center w-full py-1.5 relative transition-all duration-300 hover:scale-105 ${isActive ? 'scale-105' : ''}`}
                  >
                    <div className="flex flex-col items-center justify-center">
                      <div className="relative">
                        <item.icon className={`h-6 w-6 transition-colors ${isActive ? 'text-blue-600 dark:text-sky-400' : 'text-gray-500 dark:text-gray-400'}`} strokeWidth={isActive ? 2.5 : 2} />
                        {isOrders && unviewedOrdersCount > 0 && (
                          <span className="absolute -top-1 -right-2 bg-red-500 text-white text-[9px] font-bold rounded-full h-4 w-4 flex items-center justify-center shadow-sm border border-white dark:border-gray-800">
                            {unviewedOrdersCount > 9 ? '9+' : unviewedOrdersCount}
                          </span>
                        )}
                      </div>
                      <span className={`text-[9px] transition-colors mt-1 ${isActive ? 'text-blue-600 dark:text-sky-400 font-bold' : 'text-gray-500 dark:text-gray-400 font-medium'}`}>{item.name}</span>
                    </div>
                  </Link>
                );
              };

              return (
                <>
                  {bottomNavIds.slice(0, 2).map((id) => renderNavItem(id))}
                  {renderNavItem('catalog', true)}
                  {bottomNavIds.slice(2).map((id) => renderNavItem(id))}
                </>
              );
            })()}

            {/* 5. Menú (Más) - Fixed */}
            <button
              onClick={() => {
                setMobileMenuOpen(!mobileMenuOpen);
              }}
              className={`flex flex-col items-center justify-center w-full py-1.5 transition-all duration-300 hover:scale-105 ${mobileMenuOpen ? 'scale-105' : ''}`}
            >
              <div className="flex flex-col items-center justify-center">
                <Bars3Icon className={`h-6 w-6 transition-colors ${mobileMenuOpen ? 'text-blue-600 dark:text-sky-400' : 'text-gray-500 dark:text-gray-400'}`} strokeWidth={mobileMenuOpen ? 2.5 : 2} />
                <span className={`text-[9px] transition-colors mt-1 ${mobileMenuOpen ? 'text-blue-600 dark:text-sky-400 font-bold' : 'text-gray-500 dark:text-gray-400 font-medium'}`}>Más</span>
              </div>
            </button>
          </div>
        </div>

        {/* Mobile Full Menu Drawer (triggered by "Menú" button) */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => {
                  setMobileMenuOpen(false);
                  setIsCustomizeMode(false); // Exit edit mode on close
                }}
                className="fixed inset-0 bg-black/50 z-40 sm:hidden"
              />

              {/* Drawer Content */}
              <motion.div
                initial={{ y: '100%' }}
                animate={{ y: 0 }}
                exit={{ y: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 rounded-t-2xl z-50 sm:hidden max-h-[85vh] overflow-y-auto pb-24 shadow-2xl"
              >
                <div className="p-4">
                  <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-700 rounded-full mx-auto mb-6" />

                  <div className="flex justify-between items-center mb-4 px-2">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">{isCustomizeMode ? 'Personalizar Menú' : 'Menú Principal'}</h3>
                    <button
                      onClick={() => setIsCustomizeMode(!isCustomizeMode)}
                      className={`text-xs font-bold px-3 py-1.5 rounded-full transition-colors ${isCustomizeMode ? 'bg-blue-600 text-white shadow-lg' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                    >
                      {isCustomizeMode ? 'Terminar' : 'Personalizar'}
                    </button>
                  </div>

                  {isCustomizeMode && (
                    <div className="mb-6 animate-fadeIn">
                      <div className="px-3 py-2 bg-blue-50 text-blue-700 text-xs rounded-lg flex items-center mb-4 mx-2">
                        <span className="mr-2">ℹ️</span>
                        Organiza tus accesos directos favoritos.
                      </div>

                      <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 px-2">En Barra Inferior (Máx 3)</h4>
                      <div className="grid grid-cols-4 gap-4 px-2">
                        {bottomNavIds.map((id) => {
                          const item = NAV_ITEMS_CONFIG[id];
                          if (!item) return null;

                          // Check permissions
                          if (item.feature && !canAccess(item.feature as any)) {
                            return null;
                          }

                          // Hide Marketing for Palladium
                          if (id === 'marketing' && businessProfile?.subscription_plan === 'palladium') return null;

                          return (
                            <button
                              key={id}
                              onClick={() => handleMoveToDrawer(id)}
                              className="flex flex-col items-center gap-2 p-2 rounded-xl bg-white dark:bg-gray-800 border-2 border-blue-100 dark:border-gray-700 shadow-sm relative group"
                            >
                              <div className="p-2 bg-blue-50 dark:bg-sky-900/30 rounded-full">
                                <item.icon className="h-6 w-6 text-blue-600 dark:text-sky-400" />
                              </div>
                              <div className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-md z-10">
                                <span className="text-sm font-bold mb-0.5">-</span>
                              </div>
                              <span className="text-xs font-medium text-center text-gray-700 dark:text-gray-300">{item.name}</span>
                            </button>
                          );
                        })}

                        {/* Placeholders para espacios vacíos */}
                        {Array.from({ length: 3 - bottomNavIds.length }).map((_, i) => (
                          <div key={`empty-${i}`} className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl bg-gray-50 dark:bg-gray-800 flex flex-col items-center justify-center p-2 opacity-50">
                            <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700 mb-2"></div>
                            <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">Vacío</span>
                          </div>
                        ))}
                      </div>

                      <div className="border-t border-gray-200 dark:border-gray-700 my-6 mx-2"></div>
                      <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3 px-2">Disponibles para agregar</h4>
                    </div>
                  )}

                  <div className="grid grid-cols-4 gap-4 mb-6">
                    {drawerNavIds.map((id) => {
                      const item = NAV_ITEMS_CONFIG[id];
                      if (!item) return null;

                      // Check permissions
                      if (item.feature && !canAccess(item.feature as any)) {
                        return null;
                      }

                      // Hide Marketing for Palladium
                      if (id === 'marketing' && businessProfile?.subscription_plan === 'palladium') return null;

                      if (isCustomizeMode) {
                        const canAdd = bottomNavIds.length < 3;
                        return (
                          <button
                            key={id}
                            onClick={() => handleMoveToBottom(id)}
                            disabled={!canAdd}
                            className={`flex flex-col items-center gap-2 p-2 rounded-xl transition-all relative ${canAdd ? 'opacity-100' : 'opacity-40 grayscale'}`}
                          >
                            <div className={`p-2 bg-white dark:bg-gray-800 rounded-full shadow-sm border-2 ${canAdd ? 'border-green-400 dark:border-green-500' : 'border-gray-200 dark:border-gray-700'}`}>
                              <item.icon className={`h-6 w-6 ${item.color.includes('gray') ? 'text-gray-600 dark:text-gray-300' : item.color}`} />
                            </div>
                            {canAdd && (
                              <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-sm z-10">
                                <span className="text-xs font-bold">+</span>
                              </div>
                            )}
                            <span className="text-xs font-medium text-center text-gray-700 dark:text-gray-300">{item.name}</span>
                          </button>
                        );
                      }

                      return (
                        <Link
                          key={id}
                          to={item.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`flex flex-col items-center gap-2 p-2 rounded-xl transition-colors ${item.bgColor} dark:bg-gray-800 hover:bg-opacity-80`}
                        >
                          <div className="p-2 bg-white dark:bg-gray-900 rounded-full shadow-sm">
                            <item.icon className={`h-6 w-6 ${item.color.includes('gray') ? 'text-gray-600 dark:text-gray-300' : item.color}`} />
                          </div>
                          <span className="text-xs font-medium text-center text-gray-700 dark:text-gray-300">{item.name}</span>
                        </Link>
                      );
                    })}
                  </div>

                  <div className="space-y-1">
                    {/* Lista Completa Restante (Fixed Items) */}
                    <Link
                      to="/settings"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-xl"
                    >
                      <Settings className="h-5 w-5 mr-3 text-gray-500 dark:text-gray-400" />
                      <span className="font-medium">Configuración de Tienda</span>
                    </Link>

                    {/* BOTÓN POS MOBIL - SOLO PALLADIUM */}
                    {businessProfile?.subscription_plan === 'palladium' && (
                      <Link
                        to="/pos"
                        onClick={() => setMobileMenuOpen(false)}
                        className="flex items-center px-4 py-3 bg-slate-900 mx-2 rounded-xl text-amber-400 mt-4 border border-slate-700 shadow-lg"
                      >
                        <div className="p-1 bg-amber-500/10 rounded mr-3">
                          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <span className="font-bold">Ir a Terminal POS</span>
                      </Link>
                    )}
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>


      </nav>

      {/* Condicionar padding y background según la ruta */}
      <main className={location.pathname === '/login' ? '' : `w-full lg:max-w-none mx-auto px-4 sm:px-6 lg:px-10 ${user && location.pathname !== '/login' ? 'pt-24 lg:pt-8' : 'pt-24'} pb-24 sm:pb-8`}>
        {user && <NotificationBanner onEnableNotifications={enableNotifications} />}

        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          {user && permissions?.subscription_status === 'inactive' && location.pathname !== '/account' ? (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
              <Lock className="h-20 w-20 text-gray-400 mb-6" />
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">Funciones Bloqueadas</h1>
              <p className="text-lg text-gray-600 dark:text-gray-400 max-w-xl mx-auto mb-8">
                Para acceder a esta y otras funciones, activa tu plan.
              </p>
              <Link
                to="/pricing"
                className="bg-gradient-to-r from-sky-600 to-blue-600 text-white px-8 py-3 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all flex items-center"
              >
                <Sparkles className="h-5 w-5 mr-2" />
                Deseo pagar mi Plan
              </Link>
            </div>
          ) : (
            <Outlet />
          )}
        </motion.div>
      </main>

      {/* Panel de Notificaciones */}
      <NotificationsPanel
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
      />

      {/* Ajustes de Plataforma (Modo Oscuro / Idioma) - Cajón Móvil */}
      <AnimatePresence>
        {showPlatformSettings && (
          <>
            {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowPlatformSettings(false)}
                className="fixed inset-0 bg-black/40 backdrop-blur-sm sm:hidden"
                style={{ zIndex: 60 }}
              />

            {/* Drawer */}
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-900 rounded-t-3xl sm:hidden shadow-2xl p-6"
              style={{ zIndex: 70 }}
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center">
                  <Settings className="h-5 w-5 mr-2 text-blue-500" />
                  {t('settings.platform_settings')}
                </h3>
                <button
                  onClick={() => setShowPlatformSettings(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 bg-gray-100 dark:bg-gray-800 rounded-full"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Tema Oscuro / Claro */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    {t('settings.appearance')}
                  </h4>
                  <div className="flex bg-gray-100 dark:bg-gray-800 p-1 rounded-2xl">
                    <button
                      onClick={() => setTheme('light')}
                      className={`flex-1 py-2 flex items-center justify-center rounded-xl transition-all ${theme === 'light'
                        ? 'bg-white text-blue-600 shadow-md'
                        : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                    >
                      <Sun className="h-5 w-5 mr-2" />
                      {t('settings.light')}
                    </button>
                    <button
                      onClick={() => setTheme('dark')}
                      className={`flex-1 py-2 flex items-center justify-center rounded-xl transition-all ${theme === 'dark'
                        ? 'bg-gray-900 text-blue-400 shadow-md'
                        : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                    >
                      <Moon className="h-5 w-5 mr-2" />
                      {t('settings.dark')}
                    </button>
                  </div>
                </div>

                {/* Idioma */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    {t('settings.language')} / Language
                  </h4>
                  <div className="space-y-2">
                    <button
                      onClick={() => setLanguage('es')}
                      className={`w-full flex items-center justify-between p-4 rounded-2xl border transition-all ${language === 'es'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                    >
                      <div className="flex items-center">
                        <Globe className={`h-5 w-5 mr-3 ${language === 'es' ? 'text-blue-500' : 'text-gray-400'}`} />
                        <span className={`font-medium ${language === 'es' ? 'text-blue-700 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>
                          Español
                        </span>
                      </div>
                      {language === 'es' && <Check className="h-5 w-5 text-blue-500" />}
                    </button>

                    <button
                      onClick={() => setLanguage('en')}
                      className={`w-full flex items-center justify-between p-4 rounded-2xl border transition-all ${language === 'en'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                    >
                      <div className="flex items-center">
                        <Globe className={`h-5 w-5 mr-3 ${language === 'en' ? 'text-blue-500' : 'text-gray-400'}`} />
                        <span className={`font-medium ${language === 'en' ? 'text-blue-700 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>
                          English
                        </span>
                      </div>
                      {language === 'en' && <Check className="h-5 w-5 text-blue-500" />}
                    </button>

                  </div>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      </div>
    </div >
  );
}

export default Layout;